import client from "./client";
import type { PaginatedResponse } from "@/types/common";
import type {
  CustomLeaveType,
  CustomLeaveTypeInput,
  LeaveBalance,
  LeaveRequest,
  LeaveRequestCreateData,
  LeaveRequestStatus,
} from "@/types/leave";

export async function getBalances(
  employeeId: string,
  year?: number
): Promise<LeaveBalance[]> {
  const params: Record<string, string> = {};
  if (year) params.year = String(year);
  const res = await client.get<LeaveBalance[]>(
    `/leave/balances/${employeeId}`,
    { params }
  );
  return res.data;
}

export async function createLeaveRequest(
  data: LeaveRequestCreateData
): Promise<LeaveRequest> {
  const res = await client.post<LeaveRequest>("/leave/requests", data);
  return res.data;
}

export async function listLeaveRequests(
  page = 1,
  perPage = 20,
  employeeId?: string,
  status?: LeaveRequestStatus
): Promise<PaginatedResponse<LeaveRequest>> {
  const params: Record<string, string> = {
    page: String(page),
    per_page: String(perPage),
  };
  if (employeeId) params.employee_id = employeeId;
  if (status) params.status = status;
  const res = await client.get<PaginatedResponse<LeaveRequest>>(
    "/leave/requests",
    { params }
  );
  return res.data;
}

export async function reviewLeaveRequest(
  requestId: string,
  status: "APPROVED" | "DENIED",
  notes?: string
): Promise<void> {
  await client.put(`/leave/requests/${requestId}/approve`, {
    status,
    reviewer_notes: notes,
  });
}

/**
 * Record (or clear) the HCP-certification expiry date on a leave
 * request. Pass ``null`` to clear a stored date.
 *
 * Same permission gate as ``reviewLeaveRequest`` — HR_ADMIN /
 * SUPER_ADMIN OR managers reviewing their direct reports.
 *
 * Backend: ``PATCH /leave/requests/{id}/certification``
 * Sentinel's daily ``leave_certification_expiring`` bucket uses
 * this column directly once set; until then it falls back to the
 * 23-day-active heuristic.
 */
export async function recordLeaveCertification(
  requestId: string,
  expiresAt: string | null
): Promise<{
  id: string;
  certification_expires_at: string | null;
  reviewer_notes: string | null;
}> {
  const res = await client.patch(
    `/leave/requests/${requestId}/certification`,
    { certification_expires_at: expiresAt },
  );
  return res.data;
}

/**
 * Employee cancels their own PENDING leave request. 404 from the
 * server (already approved/denied/missing) surfaces as a thrown
 * AxiosError so the page can show a useful toast.
 */
export async function cancelLeaveRequest(requestId: string): Promise<void> {
  await client.delete(`/leave/requests/${requestId}`);
}

// ════════════════════════════════════════════════════════════════════
// Custom leave types — per-org admin-managed categories
// ════════════════════════════════════════════════════════════════════

/** List the org's custom leave types. ``includeInactive`` for the
 *  manage UI; the request-modal dropdown leaves it false so retired
 *  types don't show up. */
export async function listCustomLeaveTypes(
  includeInactive = false,
): Promise<CustomLeaveType[]> {
  const res = await client.get<CustomLeaveType[]>("/leave/custom-types", {
    params: includeInactive ? { include_inactive: true } : {},
  });
  return res.data;
}

export async function createCustomLeaveType(
  payload: CustomLeaveTypeInput,
): Promise<CustomLeaveType> {
  const res = await client.post<CustomLeaveType>(
    "/leave/custom-types",
    payload,
  );
  return res.data;
}

export async function updateCustomLeaveType(
  id: string,
  patch: Partial<CustomLeaveTypeInput>,
): Promise<CustomLeaveType> {
  const res = await client.patch<CustomLeaveType>(
    `/leave/custom-types/${id}`,
    patch,
  );
  return res.data;
}

/** Soft-delete (sets is_active=false). Hard delete intentionally
 *  not exposed — historical LeaveRequest references stay valid. */
export async function deleteCustomLeaveType(id: string): Promise<void> {
  await client.delete(`/leave/custom-types/${id}`);
}

export async function getTeamCalendar(
  startDate: string,
  endDate: string
): Promise<
  Array<{
    employee_id: string;
    employee_name: string;
    leave_type: string;
    start_date: string;
    end_date: string;
    status: string;
  }>
> {
  const res = await client.get("/leave/calendar", {
    params: { start_date: startDate, end_date: endDate },
  });
  return res.data;
}
